import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import LandingPage from './components/LandingPage';
import LoginPage from './components/LoginPage';
import Header from './components/Header';
import Dashboard from './components/Dashboard';
import AppDetailView from './components/AppDetailView';
import Profile from './components/Profile';
import ResetPasswordPage from './components/ResetPasswordPage'; // New import
import SubscriptionManagementPage from './components/SubscriptionManagementPage'; // New import
import * as api from './services/api';
import LogList from './components/LogList';
// import AuthPage from './components/AuthPage';
import AppsPage from './components/AppsPage';
import LighthousePage from './components/LighthousePage';

function App() {
  const workerApiUrl = window.REACT_APP_WORKER_API || 'http://127.0.0.1:8787';
  
  // Auth state
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true); // Start loading to check session
  const [authError, setAuthError] = useState(null);
  const [recoveryMessage, setRecoveryMessage] = useState(null); // New state for password recovery messages
  const [recoveryError, setRecoveryError] = useState(null);   // New state for password recovery errors
  const [resetMessage, setResetMessage] = useState(null);     // New state for password reset messages
  const [resetError, setResetError] = useState(null);       // New state for password reset errors

  // App data state
  const [apps, setApps] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [lighthouses, setLighthouses] = useState([]);
  const [selectedLighthouse, setSelectedLighthouse] = useState(null);

  // UI state - 'apps', 'lighthouses', or 'logs'
  const [view, setView] = useState('apps'); 

  // Routing state
  const [currentPath, setCurrentPath] = useState(window.location.pathname); 

  // Navigate to a new path
  const navigateTo = useCallback((path) => {
    window.history.pushState({}, '', path);
    setCurrentPath(path);
  }, []);

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(window.location.pathname);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Parse the current route and load the appropriate app
  useEffect(() => {
    if (!user) {
      // If not authenticated and trying to access protected routes, redirect to login
      if (currentPath.startsWith('/dashboard') || 
          currentPath.startsWith('/apps/') || 
          currentPath.startsWith('/profile')) {
        navigateTo('/login');
      }
      return;
    }

    // Handle app detail routes
    const match = currentPath.match(/^\/apps\/([^/]+)/);
    if (match && apps.length) {
      const appId = match[1];
      const app = apps.find(a => a.id === appId);
      if (app) {
        setSelectedApp(app);
      } else {
        // App not found, redirect to dashboard
        navigateTo('/dashboard');
      }
    } else if (currentPath === '/dashboard') {
      setSelectedApp(null);
    } else if (currentPath === '/profile') {
      // Profile route doesn't need app selection, ensure no app is selected
      setSelectedApp(null);
    } else if (currentPath === '/subscription') {
      // Subscription route doesn't need app selection
      setSelectedApp(null);
    }
    // Profile route doesn't need app selection
  }, [currentPath, apps, user, navigateTo]);

  // Check for existing session on initial load
  useEffect(() => {
    const checkUserSession = async () => {
      try {
        const user = await api.getMe(workerApiUrl);
        setUser(user);
        // After getting user, fetch their apps
        const userApps = await api.getApps(workerApiUrl);
        setApps(userApps);
        const userLighthouses = await api.getLighthouses(workerApiUrl);
        setLighthouses(userLighthouses);
      } catch (err) {
        setUser(null);
      } finally {
        setAuthLoading(false);
      }
    };
    checkUserSession();
  }, [workerApiUrl]);

  const handleLogin = useCallback(async (credentials) => {
    setAuthError(null);
    setAuthLoading(true);
    try {
      const data = await api.login(workerApiUrl, credentials);
      setUser(data.user);
      
      // Fetch user's apps after successful login
      try {
        const userApps = await api.getApps(workerApiUrl);
        setApps(userApps);
        const userLighthouses = await api.getLighthouses(workerApiUrl);
        setLighthouses(userLighthouses);
      } catch (appsErr) {
        console.error('Failed to fetch apps after login:', appsErr);
      }
      
      // Redirect to dashboard after successful login
      navigateTo('/dashboard');
    } catch (err) {
      setAuthError(err.message);
    } finally {
      setAuthLoading(false);
    }
  }, [workerApiUrl, navigateTo]);

  const handleRegister = useCallback(async (credentials) => {
    setAuthError(null);
    setAuthLoading(true);
    try {
      await api.register(workerApiUrl, credentials);
      alert('Registration successful! Please check your email to confirm your account before logging in.');
    } catch (err) {
      setAuthError(err.message);
    } finally {
      setAuthLoading(false);
    }
  }, [workerApiUrl]);

  const handleForgotPassword = useCallback(async (email) => {
    setRecoveryMessage(null);
    setRecoveryError(null);
    setAuthLoading(true); // Use authLoading for recovery process as well
    try {
      const response = await api.forgotPassword(workerApiUrl, email);
      setRecoveryMessage(response.message || 'Password reset email sent. Please check your inbox.');
      // Optionally, you might want to redirect or show a success page here
    } catch (err) {
      setRecoveryError(err.message || 'Failed to send password reset email.');
    } finally {
      setAuthLoading(false);
    }
  }, [workerApiUrl]);

  const handleResetPassword = useCallback(async ({ token, newPassword }) => {
    setResetMessage(null);
    setResetError(null);
    setAuthLoading(true);
    try {
      const response = await api.resetPassword(workerApiUrl, { token, newPassword });
      setResetMessage(response.message || 'Password updated successfully. You can now log in.');
      // Optionally redirect to login page after successful reset
      navigateTo('/login');
    } catch (err) {
      setResetError(err.message || 'Failed to reset password.');
    } finally {
      setAuthLoading(false);
    }
  }, [workerApiUrl, navigateTo]);

  const handleLogout = useCallback(async () => {
    try {
      await api.logout(workerApiUrl);
      setUser(null);
      setApps([]);
      setSelectedApp(null);
      navigateTo('/');
    } catch (err) {
      setAuthError('Failed to log out.');
    }
  }, [workerApiUrl, navigateTo]);
  
  const handleSelectApp = (app) => {
    navigateTo(`/apps/${app.id}`);
  };

  const handleSelectLighthouse = (lighthouse) => {
    
    navigateTo(`/lighthouses/${lighthouse.id}`);
  };

  const handleBackToDashboard = () => {
    navigateTo('/dashboard');
  };

  const handleGoToProfile = () => {
    navigateTo('/profile');
  };

  const handleGoToSubscriptionManagement = () => {
    navigateTo('/subscription');
  };

  const handleGoToLogin = () => {
    navigateTo('/login');
  };

  const handleGetStarted = () => {
    navigateTo('/login');
  };

  const handleAccountDeleted = async () => {
    await handleLogout();
    navigateTo('/');
  };

  const refreshUserData = async () => {
    try {
      const user = await api.getMe(workerApiUrl);
      setUser(user);
      const userApps = await api.getApps(workerApiUrl);
      setApps(userApps);
      const userLighthouses = await api.getLighthouses(workerApiUrl);
      setLighthouses(userLighthouses);
    } catch (err) {
      console.error('Failed to refresh user data:', err);
    }
  };

  const refreshApps = async () => {
    try {
      const userApps = await api.getApps(workerApiUrl);
      setApps(userApps);
    } catch (err) {
      console.error('Failed to refresh apps:', err);
    }
  };

  const refreshLighthouses = async () => {
    try {
      const userLighthouses = await api.getLighthouses(workerApiUrl);
      setLighthouses(userLighthouses);
    } catch (err) {
      console.error('Failed to refresh lighthouses:', err);
    }
  };
  // Render logic based on authentication and current path
  if (authLoading) {
    return <div className="loading-fullscreen">Loading...</div>;
  }

  // Handle unauthenticated users: show LandingPage or LoginPage
  if (!user) {
    if (currentPath === '/' || currentPath === '') {
      return <LandingPage onGetStarted={handleGetStarted} onLogin={handleGoToLogin} />;
    } else if (currentPath === '/login') {
      return (
        <LoginPage
          onLogin={handleLogin}
          onRegister={handleRegister}
          onForgotPassword={handleForgotPassword} // New prop for password recovery
          error={authError}
          isLoading={authLoading}
          recoveryMessage={recoveryMessage} // New prop for recovery messages
          recoveryError={recoveryError}   // New prop for recovery errors
          onBack={() => navigateTo('/')}
        />
      );
    } else if (currentPath === '/reset-password') { // New route for password reset
      return (
        <ResetPasswordPage
          onResetPassword={handleResetPassword}
          isLoading={authLoading}
          resetMessage={resetMessage}
          resetError={resetError}
          onBack={() => navigateTo('/login')}
        />
      );
    }
    else {
      // For any other path, redirect to login if not authenticated
      navigateTo('/login');
      return null; // Or a loading spinner if you prefer
    }
  }

  // If user is authenticated, show the appropriate view with Header
  const showDashboard = currentPath === '/dashboard';
  const showAppDetail = selectedApp !== null && currentPath.startsWith('/apps/');
  const showProfile = currentPath === '/profile';
  const showLighthouseDetail =  currentPath.startsWith('/lighthouses');
  const showSubscriptionManagement = currentPath === '/subscription';

  return (
    <div className="app-layout">
      <Header 
        user={user} 
        onLogout={handleLogout}
        onProfileClick={handleGoToProfile}
        onLogoClick={() => navigateTo('/dashboard')} // Logo always goes to dashboard when logged in
      />
      <main className="main-content">
        {showDashboard && (
          <Dashboard
            apps={apps}
            lighthouses={lighthouses}
            onSelectLighthouse={handleSelectLighthouse}
            onRefreshLighthouses={refreshLighthouses}
            onSelectApp={handleSelectApp}
            onRefreshApps={refreshApps}
            workerApiUrl={workerApiUrl}
            user={user} // Pass user object to Dashboard
          />
        )}
        {showAppDetail && (
          <AppDetailView
            key={selectedApp.id}
            selectedApp={selectedApp}
            workerApiUrl={workerApiUrl}
            onBack={handleBackToDashboard}
          />
        )}
        {showProfile && (
          <Profile
            user={user}
            workerApiUrl={workerApiUrl}
            onBack={handleBackToDashboard}
            onAccountDeleted={handleAccountDeleted}
            onManageSubscription={handleGoToSubscriptionManagement} // New prop
          />
        )}

        {showLighthouseDetail && (
          // <Lighthouse
          //   key={selectedLighthouse.id}
          //   selectedLighthouse={selectedLighthouse}
          //   workerApiUrl={workerApiUrl}
          //   onBack={handleBackToDashboard}
          // />
          <LighthousePage
            workerApiUrl={workerApiUrl}
            onBack={handleBackToDashboard}
            onSelectLighthouse={setSelectedLighthouse}
            selectedLighthouse={selectedLighthouse}
          />
        )}
        {showSubscriptionManagement && (
          <SubscriptionManagementPage
            user={user}
            workerApiUrl={workerApiUrl}
            onBack={handleGoToProfile}
            onSubscriptionChange={refreshUserData}
          />
        )}
      </main>
    </div>
  );
}

export default App;
